/*
    This file is part of @erc725/erc725.js.
    @erc725/erc725.js is free software: you can redistribute it and/or modify
    it under the terms of the GNU Lesser General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.
    @erc725/erc725.js is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU Lesser General Public License for more details.
    You should have received a copy of the GNU Lesser General Public License
    along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
/**
 * @file lib/utils.ts
 * @author Robert McLeod <@robertdavid010>, Fabian Vogelsteller <fabian@lukso.network>
 * @date 2020
 */
import { checkAddressChecksum, isAddress, keccak256, leftPad, numberToHex, padLeft, } from 'web3-utils';
import { HASH_FUNCTIONS, SUPPORTED_HASH_FUNCTIONS_LIST, } from './constants';
import { decodeValueContent, decodeValueType, encodeValueContent, encodeValueType, valueContentEncodingMap as valueContentMap, } from './encoder';
/**
 *
 * @param schemaElementDefinition An object of the schema for this key
 * @param value can contain single value, or an object as required by schema (JSONURL, or ASSETURL)
 * @return the encoded value as per the schema
 */
export function encodeKeyValue(schemaElementDefinition, value) {
    // Check if existing in the supported valueContent mapping.
    if (!valueContentMap[schemaElementDefinition.valueContent] &&
        schemaElementDefinition.valueContent.substr(0, 2) !== '0x') {
        throw new Error(`The valueContent '${schemaElementDefinition.valueContent} 
            for ${schemaElementDefinition.name} is not supported.`);
    }
    let result;
    const sameEncoding = valueContentMap[schemaElementDefinition.valueContent] &&
        valueContentMap[schemaElementDefinition.valueContent].type ===
            schemaElementDefinition.valueType.split('[]')[0];
    const isArray = schemaElementDefinition.valueType.substr(schemaElementDefinition.valueType.length - 2) === '[]';
    // We only loop if the valueType done by abi.encodeParameter can not handle it directly
    if (Array.isArray(value) && !sameEncoding) {
        // value type encoding will handle it?
        // we handle an array element encoding
        const results = [];
        for (let index = 0; index < value.length; index++) {
            const element = value[index];
            results.push(encodeValueContent(schemaElementDefinition.valueContent, element));
        }
        result = results;
    }
    else if (!isArray) {
        // Straight forward encode
        result = encodeValueContent(schemaElementDefinition.valueContent, value);
    }
    else if (sameEncoding) {
        result = value; // leaving this for below
    }
    if (
    // and we only skip bytes regardless
    schemaElementDefinition.valueType !== 'bytes' &&
        // Requires encoding because !sameEncoding means both encodings are required
        !sameEncoding) {
        result = encodeValueType(schemaElementDefinition.valueType, result);
    }
    else if (isArray && sameEncoding) {
        result = encodeValueType(schemaElementDefinition.valueType, result);
    }
    return result;
}
/**
 *
 * @param key The schema key of a schema with keyType = 'Array'
 * @param index An integer representing the intended array index
 * @return The raw bytes key for the array element
 */
export function encodeArrayKey(key, index) {
    return key.substr(0, 34) + padLeft(numberToHex(index), 32).replace('0x', '');
}
/**
 *
 * @param name the schema element name
 * @return the name of the key encoded as per specifications
 * @return a string of the encoded schema name
 */
export function encodeKeyName(name) {
    const colon = name.indexOf(':');
    // if name:subname, then construct using bytes16(hashFirstWord) + bytes12(0) + bytes4(hashLastWord)
    return colon !== -1
        ? keccak256(name.substr(0, colon)).substr(0, 34) +
            leftPad(keccak256(name.substr(colon + 1)).substr(2, 8), 32)
        : keccak256(name); // otherwise just bytes32(hash)
}
/**
 *
 * @param schemas An array of objects
 * @param {string} key A string of either the schema element name, or key
 * @return The requested schema element from the full array of schemas
 */
export function getSchemaElement(schemas, key) {
    const keyHash = key.substr(0, 2) !== '0x' ? encodeKeyName(key) : key;
    const schemaElement = schemas.find((e) => e.key === keyHash);
    if (!schemaElement) {
        throw new Error('No matching schema found for key: "' + key + '" (' + keyHash + ').');
    }
    return schemaElement;
}
/**
 *
 * @param schema An object of a schema definition that must have a keyType of 'Array'
 * @param index The index of the array element to transpose the schema to
 * @return Modified schema element of keyType 'Singleton' for fetching or decoding/encoding the array element
 */
export function transposeArraySchema(schema, index) {
    // Use enum ERC725JSONSchemaKeyType instead?
    if (schema.keyType.toLowerCase() !== 'array') {
        console.error('Schema is not of keyType "Array" for schema: "' + schema.name + '".');
    }
    return {
        name: schema.name,
        key: encodeArrayKey(schema.key, index),
        keyType: 'Singleton',
        // TODO: This can be solved by defining an extra "Erc725ArraySchema" for array
        // @ts-ignore
        valueContent: schema.elementValueContent,
        // @ts-ignore
        valueType: schema.elementValueType,
    };
}
/**
 *
 * @param schema is an object of a schema definitions
 * @param value will be either key-value pairs for a key type of Array, or a single value for type Singleton
 * @return the encoded value for the key as per the supplied schema
 */
export function encodeKey(schema, value) {
    // NOTE: This will not guarantee order of array as on chain. Assumes developer must set correct order
    if (schema.keyType.toLowerCase() === 'array' && Array.isArray(value)) {
        const results = [];
        for (let index = 0; index < value.length; index++) {
            const dataElement = value[index];
            if (index === 0) {
                // This is arrayLength as the first element in the raw array
                results.push({
                    key: schema.key,
                    // @ts-ignore
                    value: encodeKeyValue(schema, value.length), // the array length
                });
            }
            const newSchema = transposeArraySchema(schema, index);
            results.push({
                key: newSchema.key,
                value: encodeKeyValue(newSchema, dataElement),
            });
        }
        return results;
    }
    if (schema.keyType.toLowerCase() === 'singleton' ||
        schema.keyType.toLowerCase() === 'mapping') {
        return encodeKeyValue(schema, value);
    }
    console.error('Incorrect data match or keyType in schema from encodeKey(): "' +
        schema.keyType +
        '"');
    return null;
}
/**
 *
 * @param schemaElementDefinition An object of the schema for this key
 * @param value the value to decode
 * @return the decoded value as per the schema
 */
export function decodeKeyValue(schemaElementDefinition, value) {
    // Check for the missing map.
    if (!valueContentMap[schemaElementDefinition.valueContent] &&
        schemaElementDefinition.valueContent.substr(0, 2) !== '0x') {
        throw new Error('The valueContent "' +
            schemaElementDefinition.valueContent +
            '" for "' +
            schemaElementDefinition.name +
            '" is not supported.');
    }
    let sameEncoding = valueContentMap[schemaElementDefinition.valueContent] &&
        valueContentMap[schemaElementDefinition.valueContent].type ===
            schemaElementDefinition.valueType.split('[]')[0];
    const isArray = schemaElementDefinition.valueType.substr(schemaElementDefinition.valueType.length - 2) === '[]';
    // VALUE TYPE
    if (schemaElementDefinition.valueType !== 'bytes' && // we ignore because all is decoded by bytes to start with (abi)
        schemaElementDefinition.valueType !== 'string' &&
        !isAddress(value) // checks for addresses, since technically an address is bytes?
    ) {
        // eslint-disable-next-line no-param-reassign
        value = decodeValueType(schemaElementDefinition.valueType, value);
    }
    // As per exception above, if address and sameEncoding, then the address still needs to be handled
    if (sameEncoding && isAddress(value) && !checkAddressChecksum(value)) {
        sameEncoding = !sameEncoding;
    }
    if (sameEncoding && schemaElementDefinition.valueType !== 'string') {
        return value;
    }
    // VALUE CONTENT
    // We are finished if duplicated encoding methods
    if (isArray && Array.isArray(value)) {
        // value must be an array also
        const results = [];
        for (let index = 0; index < value.length; index++) {
            const element = value[index];
            results.push(decodeValueContent(schemaElementDefinition.valueContent, element));
        }
        return results;
    }
    return decodeValueContent(schemaElementDefinition.valueContent, value);
}
/**
 *
 * @param schema is an object of a schema definitions
 * @param value will be either key-value pairs for a key type of Array, or a single value for type Singleton
 * @return the decoded value/values as per the schema definition
 */
export function decodeKey(schema, value) {
    if (schema.keyType.toLowerCase() === 'array') {
        const results = [];
        const valueElement = value.find((e) => e.key === schema.key);
        // Handle empty/non-existent array
        if (!valueElement) {
            return results;
        }
        const arrayLength = decodeKeyValue(schema, valueElement.value) || 0;
        // This will not run if no match or arrayLength
        for (let index = 0; index < arrayLength; index++) {
            const newSchema = transposeArraySchema(schema, index);
            const dataElement = value.find((e) => e.key === newSchema.key);
            if (dataElement) {
                results.push(decodeKeyValue(newSchema, dataElement.value));
            }
        } // end for loop
        return results;
    }
    if (schema.keyType.toLowerCase() === 'singleton' ||
        schema.keyType.toLowerCase() === 'mapping') {
        if (Array.isArray(value)) {
            const newValue = value.find((e) => e.key === schema.key);
            // Handle empty or non-values
            if (!newValue) {
                return null;
            }
            return decodeKeyValue(schema, newValue.value);
        }
        return decodeKeyValue(schema, value);
    }
    console.error('Incorrect data match or keyType in schema from decodeKey(): "' +
        schema.keyType +
        '"');
    return null;
}
/**
 *
 * @param schema schema is an array of objects of schema definitions
 * @param data data is an array of objects of key-value pairs
 * @return: all decoded data as per required by the schema and provided data
 */
export function decodeData(data, schema) {
    return Object.entries(data).reduce((decodedData, [key, value]) => {
        const schemaElement = getSchemaElement(schema, key);
        return Object.assign(Object.assign({}, decodedData), { [schemaElement.name]: decodeKey(schemaElement, value) });
    }, {});
}
/**
 * @param schema an array of schema definitions as per ${@link ERC725JSONSchema}
 * @param data an object of key-value pairs
 */
export function encodeData(data, schema) {
    return Object.entries(data).reduce((accumulator, [key, value]) => {
        const schemaElement = getSchemaElement(schema, key);
        accumulator[key] = {
            value: encodeKey(schemaElement, value),
            key: schemaElement.key,
        };
        return accumulator;
    }, {});
}
export function getHashFunction(hashFunctionNameOrHash) {
    const hashFunction = HASH_FUNCTIONS[hashFunctionNameOrHash];
    if (!hashFunction) {
        throw new Error(`Chosen hashFunction '${hashFunctionNameOrHash}' is not supported.
      Supported hashFunctions: ${SUPPORTED_HASH_FUNCTIONS_LIST}
      `);
    }
    return hashFunction;
}
export function hashData(data, hashFunctionNameOrHash) {
    const hashFunction = getHashFunction(hashFunctionNameOrHash);
    return hashFunction.method(data);
}
/**
 * Hashes the data received with the specified hashing function,
 * and compares the result with the provided hash.
 *
 * @throws *Error* in case of a mismatch of the hashes.
 */
export function isDataAuthentic(data, expectedHash, lowerCaseHashFunction) {
    const jsonHash = hashData(data, lowerCaseHashFunction);
    if (jsonHash !== expectedHash) {
        console.error(`Hash mismatch, returned JSON hash ("${jsonHash}") is different from expected hash "${expectedHash}"`);
        return false;
    }
    return true;
}
/**
 * Transform the object containing the encoded data into an array ordered by keys,
 * for easier handling when writing the data to the blockchain.
 *
 * @param {{
 *   [key: string]: any;
 * }} encodedData This is essentially the object you receive when calling `encodeData(...)`
 * @return {*}  KeyValuePair[] An array of key-value objects
 */
export function flattenEncodedData(encodedData) {
    return (Object.entries(encodedData)
        .reduce((keyValuePairs, [, encodedDataElement]) => {
        if (Array.isArray(encodedDataElement.value)) {
            return keyValuePairs.concat(encodedDataElement.value);
        }
        keyValuePairs.push({
            key: encodedDataElement.key,
            value: encodedDataElement.value,
        });
        return keyValuePairs;
    }, [])
        // sort array of objects by keys, to not be dependent on the order of the object's keys
        .sort((a, b) => {
        if (a.key < b.key)
            return -1;
        return a.key > b.key ? 1 : 0;
    }));
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXRpbHMuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvbGliL3V0aWxzLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7Ozs7Ozs7Ozs7RUFZRTtBQUNGOzs7O0dBSUc7QUFFSCxPQUFPLEVBQ0wsb0JBQW9CLEVBQ3BCLFNBQVMsRUFDVCxTQUFTLEVBQ1QsT0FBTyxFQUNQLFdBQVcsRUFDWCxPQUFPLEdBQ1IsTUFBTSxZQUFZLENBQUM7QUFHcEIsT0FBTyxFQUNMLGNBQWMsRUFFZCw2QkFBNkIsR0FDOUIsTUFBTSxhQUFhLENBQUM7QUFFckIsT0FBTyxFQUNMLGtCQUFrQixFQUNsQixlQUFlLEVBQ2Ysa0JBQWtCLEVBQ2xCLGVBQWUsRUFDZix1QkFBdUIsSUFBSSxlQUFlLEdBQzNDLE1BQU0sV0FBVyxDQUFDO0FBT25COzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLGNBQWMsQ0FDNUIsdUJBQTJDLEVBQzNDLEtBQWE7SUFFYiwyREFBMkQ7SUFDM0QsSUFDRSxDQUFDLGVBQWUsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLENBQUM7UUFDdEQsdUJBQXVCLENBQUMsWUFBWSxDQUFDLE1BQU0sQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLEtBQUssSUFBSSxFQUMxRDtRQUNBLE1BQU0sSUFBSSxLQUFLLENBQ2IscUJBQXFCLHVCQUF1QixDQUFDLFlBQVk7a0JBQzdDLHVCQUF1QixDQUFDLElBQUksb0JBQW9CLENBQzdELENBQUM7S0FDSDtJQUVELElBQUksTUFBTSxDQUFDO0lBQ1gsTUFBTSxZQUFZLEdBQ2hCLGVBQWUsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLENBQUM7UUFDckQsZUFBZSxDQUFDLHVCQUF1QixDQUFDLFlBQVksQ0FBQyxDQUFDLElBQUk7WUFDeEQsdUJBQXVCLENBQUMsU0FBUyxDQUFDLEtBQUssQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQztJQUNyRCxNQUFNLE9BQU8sR0FDWCx1QkFBdUIsQ0FBQyxTQUFTLENBQUMsTUFBTSxDQUN0Qyx1QkFBdUIsQ0FBQyxTQUFTLENBQUMsTUFBTSxHQUFHLENBQUMsQ0FDN0MsS0FBSyxJQUFJLENBQUM7SUFFYix1RkFBdUY7SUFDdkYsSUFBSSxLQUFLLENBQUMsT0FBTyxDQUFDLEtBQUssQ0FBQyxJQUFJLENBQUMsWUFBWSxFQUFFO1FBQ3pDLHNDQUFzQztRQUV0QyxzQ0FBc0M7UUFDdEMsTUFBTSxPQUFPLEdBQXVCLEVBQUUsQ0FBQztRQUN2QyxLQUFLLElBQUksS0FBSyxHQUFHLENBQUMsRUFBRSxLQUFLLEdBQUcsS0FBSyxDQUFDLE1BQU0sRUFBRSxLQUFLLEVBQUUsRUFBRTtZQUNqRCxNQUFNLE9BQU8sR0FBRyxLQUFLLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDN0IsT0FBTyxDQUFDLElBQUksQ0FDVixrQkFBa0IsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLEVBQUUsT0FBTyxDQUFDLENBQ2xFLENBQUM7U0FDSDtRQUNELE1BQU0sR0FBRyxPQUFPLENBQUM7S0FDbEI7U0FBTSxJQUFJLENBQUMsT0FBTyxFQUFFO1FBQ25CLDBCQUEwQjtRQUMxQixNQUFNLEdBQUcsa0JBQWtCLENBQUMsdUJBQXVCLENBQUMsWUFBWSxFQUFFLEtBQUssQ0FBQyxDQUFDO0tBQzFFO1NBQU0sSUFBSSxZQUFZLEVBQUU7UUFDdkIsTUFBTSxHQUFHLEtBQUssQ0FBQyxDQUFDLHlCQUF5QjtLQUMxQztJQUVEO0lBQ0Usb0NBQW9DO0lBQ3BDLHVCQUF1QixDQUFDLFNBQVMsS0FBSyxPQUFPO1FBQzdDLDRFQUE0RTtRQUM1RSxDQUFDLFlBQVksRUFDYjtRQUNBLE1BQU0sR0FBRyxlQUFlLENBQUMsdUJBQXVCLENBQUMsU0FBUyxFQUFFLE1BQU0sQ0FBQyxDQUFDO0tBQ3JFO1NBQU0sSUFBSSxPQUFPLElBQUksWUFBWSxFQUFFO1FBQ2xDLE1BQU0sR0FBRyxlQUFlLENBQUMsdUJBQXVCLENBQUMsU0FBUyxFQUFFLE1BQU0sQ0FBQyxDQUFDO0tBQ3JFO0lBRUQsT0FBTyxNQUFNLENBQUM7QUFDaEIsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLGNBQWMsQ0FBQyxHQUFXLEVBQUUsS0FBYTtJQUN2RCxPQUFPLEdBQUcsQ0FBQyxNQUFNLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxHQUFHLE9BQU8sQ0FBQyxXQUFXLENBQUMsS0FBSyxDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUMsT0FBTyxDQUFDLElBQUksRUFBRSxFQUFFLENBQUMsQ0FBQztBQUMvRSxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsYUFBYSxDQUFDLElBQVk7SUFDeEMsTUFBTSxLQUFLLEdBQUcsSUFBSSxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQUMsQ0FBQztJQUNoQyxtR0FBbUc7SUFDbkcsT0FBTyxLQUFLLEtBQUssQ0FBQyxDQUFDO1FBQ2pCLENBQUMsQ0FBQyxTQUFTLENBQUMsSUFBSSxDQUFDLE1BQU0sQ0FBQyxDQUFDLEVBQUUsS0FBSyxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQztZQUM1QyxPQUFPLENBQUMsU0FBUyxDQUFDLElBQUksQ0FBQyxNQUFNLENBQUMsS0FBSyxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUMsTUFBTSxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsRUFBRSxFQUFFLENBQUM7UUFDL0QsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLCtCQUErQjtBQUN0RCxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsZ0JBQWdCLENBQUMsT0FBMkIsRUFBRSxHQUFXO0lBQ3ZFLE1BQU0sT0FBTyxHQUFHLEdBQUcsQ0FBQyxNQUFNLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxLQUFLLElBQUksQ0FBQyxDQUFDLENBQUMsYUFBYSxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQyxHQUFHLENBQUM7SUFDckUsTUFBTSxhQUFhLEdBQUcsT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDLENBQUMsRUFBRSxFQUFFLENBQUMsQ0FBQyxDQUFDLEdBQUcsS0FBSyxPQUFPLENBQUMsQ0FBQztJQUM3RCxJQUFJLENBQUMsYUFBYSxFQUFFO1FBQ2xCLE1BQU0sSUFBSSxLQUFLLENBQ2IscUNBQXFDLEdBQUcsR0FBRyxHQUFHLEtBQUssR0FBRyxPQUFPLEdBQUcsSUFBSSxDQUNyRSxDQUFDO0tBQ0g7SUFFRCxPQUFPLGFBQWEsQ0FBQztBQUN2QixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsb0JBQW9CLENBQ2xDLE1BQXdCLEVBQ3hCLEtBQWE7SUFFYiw0Q0FBNEM7SUFDNUMsSUFBSSxNQUFNLENBQUMsT0FBTyxDQUFDLFdBQVcsRUFBRSxLQUFLLE9BQU8sRUFBRTtRQUM1QyxPQUFPLENBQUMsS0FBSyxDQUNYLGdEQUFnRCxHQUFHLE1BQU0sQ0FBQyxJQUFJLEdBQUcsSUFBSSxDQUN0RSxDQUFDO0tBQ0g7SUFFRCxPQUFPO1FBQ0wsSUFBSSxFQUFFLE1BQU0sQ0FBQyxJQUFJO1FBQ2pCLEdBQUcsRUFBRSxjQUFjLENBQUMsTUFBTSxDQUFDLEdBQUcsRUFBRSxLQUFLLENBQUM7UUFDdEMsT0FBTyxFQUFFLFdBQVc7UUFDcEIsOEVBQThFO1FBQzlFLGFBQWE7UUFDYixZQUFZLEVBQUUsTUFBTSxDQUFDLG1CQUFtQjtRQUN4QyxhQUFhO1FBQ2IsU0FBUyxFQUFFLE1BQU0sQ0FBQyxnQkFBZ0I7S0FDbkMsQ0FBQztBQUNKLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sVUFBVSxTQUFTLENBQUMsTUFBd0IsRUFBRSxLQUFLO0lBQ3ZELHFHQUFxRztJQUNyRyxJQUFJLE1BQU0sQ0FBQyxPQUFPLENBQUMsV0FBVyxFQUFFLEtBQUssT0FBTyxJQUFJLEtBQUssQ0FBQyxPQUFPLENBQUMsS0FBSyxDQUFDLEVBQUU7UUFDcEUsTUFBTSxPQUFPLEdBQXFDLEVBQUUsQ0FBQztRQUVyRCxLQUFLLElBQUksS0FBSyxHQUFHLENBQUMsRUFBRSxLQUFLLEdBQUcsS0FBSyxDQUFDLE1BQU0sRUFBRSxLQUFLLEVBQUUsRUFBRTtZQUNqRCxNQUFNLFdBQVcsR0FBRyxLQUFLLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDakMsSUFBSSxLQUFLLEtBQUssQ0FBQyxFQUFFO2dCQUNmLDREQUE0RDtnQkFDNUQsT0FBTyxDQUFDLElBQUksQ0FBQztvQkFDWCxHQUFHLEVBQUUsTUFBTSxDQUFDLEdBQUc7b0JBQ2YsYUFBYTtvQkFDYixLQUFLLEVBQUUsY0FBYyxDQUFDLE1BQU0sRUFBRSxLQUFLLENBQUMsTUFBTSxDQUFDLEVBQUUsbUJBQW1CO2lCQUNqRSxDQUFDLENBQUM7YUFDSjtZQUVELE1BQU0sU0FBUyxHQUFHLG9CQUFvQixDQUFDLE1BQU0sRUFBRSxLQUFLLENBQUMsQ0FBQztZQUN0RCxPQUFPLENBQUMsSUFBSSxDQUFDO2dCQUNYLEdBQUcsRUFBRSxTQUFTLENBQUMsR0FBRztnQkFDbEIsS0FBSyxFQUFFLGNBQWMsQ0FBQyxTQUFTLEVBQUUsV0FBVyxDQUFDO2FBQzlDLENBQUMsQ0FBQztTQUNKO1FBRUQsT0FBTyxPQUFPLENBQUM7S0FDaEI7SUFFRCxJQUNFLE1BQU0sQ0FBQyxPQUFPLENBQUMsV0FBVyxFQUFFLEtBQUssV0FBVztRQUM1QyxNQUFNLENBQUMsT0FBTyxDQUFDLFdBQVcsRUFBRSxLQUFLLFNBQVMsRUFDMUM7UUFDQSxPQUFPLGNBQWMsQ0FBQyxNQUFNLEVBQUUsS0FBSyxDQUFDLENBQUM7S0FDdEM7SUFFRCxPQUFPLENBQUMsS0FBSyxDQUNYLCtEQUErRDtRQUM3RCxNQUFNLENBQUMsT0FBTztRQUNkLEdBQUcsQ0FDTixDQUFDO0lBQ0YsT0FBTyxJQUFJLENBQUM7QUFDZCxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsY0FBYyxDQUFDLHVCQUF1QixFQUFFLEtBQUs7SUFDM0QsNkJBQTZCO0lBQzdCLElBQ0UsQ0FBQyxlQUFlLENBQUMsdUJBQXVCLENBQUMsWUFBWSxDQUFDO1FBQ3RELHVCQUF1QixDQUFDLFlBQVksQ0FBQyxNQUFNLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxLQUFLLElBQUksRUFDMUQ7UUFDQSxNQUFNLElBQUksS0FBSyxDQUNiLG9CQUFvQjtZQUNsQix1QkFBdUIsQ0FBQyxZQUFZO1lBQ3BDLFNBQVM7WUFDVCx1QkFBdUIsQ0FBQyxJQUFJO1lBQzVCLHFCQUFxQixDQUN4QixDQUFDO0tBQ0g7SUFFRCxJQUFJLFlBQVksR0FDZCxlQUFlLENBQUMsdUJBQXVCLENBQUMsWUFBWSxDQUFDO1FBQ3JELGVBQWUsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLENBQUMsQ0FBQyxJQUFJO1lBQ3hELHVCQUF1QixDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7SUFDckQsTUFBTSxPQUFPLEdBQ1gsdUJBQXVCLENBQUMsU0FBUyxDQUFDLE1BQU0sQ0FDdEMsdUJBQXVCLENBQUMsU0FBUyxDQUFDLE1BQU0sR0FBRyxDQUFDLENBQzdDLEtBQUssSUFBSSxDQUFDO0lBRWIsYUFBYTtJQUNiLElBQ0UsdUJBQXVCLENBQUMsU0FBUyxLQUFLLE9BQU8sSUFBSSxnRUFBZ0U7UUFDakgsdUJBQXVCLENBQUMsU0FBUyxLQUFLLFFBQVE7UUFDOUMsQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLENBQUMsK0RBQStEO01BQ2pGO1FBQ0EsNkNBQTZDO1FBQzdDLEtBQUssR0FBRyxlQUFlLENBQUMsdUJBQXVCLENBQUMsU0FBUyxFQUFFLEtBQUssQ0FBQyxDQUFDO0tBQ25FO0lBRUQsa0dBQWtHO0lBQ2xHLElBQUksWUFBWSxJQUFJLFNBQVMsQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLG9CQUFvQixDQUFDLEtBQUssQ0FBQyxFQUFFO1FBQ3BFLFlBQVksR0FBRyxDQUFDLFlBQVksQ0FBQztLQUM5QjtJQUVELElBQUksWUFBWSxJQUFJLHVCQUF1QixDQUFDLFNBQVMsS0FBSyxRQUFRLEVBQUU7UUFDbEUsT0FBTyxLQUFLLENBQUM7S0FDZDtJQUVELGdCQUFnQjtJQUNoQixpREFBaUQ7SUFFakQsSUFBSSxPQUFPLElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUMsRUFBRTtRQUNuQyw4QkFBOEI7UUFDOUIsTUFBTSxPQUFPLEdBQXVCLEVBQUUsQ0FBQztRQUV2QyxLQUFLLElBQUksS0FBSyxHQUFHLENBQUMsRUFBRSxLQUFLLEdBQUcsS0FBSyxDQUFDLE1BQU0sRUFBRSxLQUFLLEVBQUUsRUFBRTtZQUNqRCxNQUFNLE9BQU8sR0FBRyxLQUFLLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDN0IsT0FBTyxDQUFDLElBQUksQ0FDVixrQkFBa0IsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLEVBQUUsT0FBTyxDQUFDLENBQ2xFLENBQUM7U0FDSDtRQUVELE9BQU8sT0FBTyxDQUFDO0tBQ2hCO0lBRUQsT0FBTyxrQkFBa0IsQ0FBQyx1QkFBdUIsQ0FBQyxZQUFZLEVBQUUsS0FBSyxDQUFDLENBQUM7QUFDekUsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLFNBQVMsQ0FBQyxNQUF3QixFQUFFLEtBQUs7SUFDdkQsSUFBSSxNQUFNLENBQUMsT0FBTyxDQUFDLFdBQVcsRUFBRSxLQUFLLE9BQU8sRUFBRTtRQUM1QyxNQUFNLE9BQU8sR0FBVSxFQUFFLENBQUM7UUFDMUIsTUFBTSxZQUFZLEdBQUcsS0FBSyxDQUFDLElBQUksQ0FBQyxDQUFDLENBQUMsRUFBRSxFQUFFLENBQUMsQ0FBQyxDQUFDLEdBQUcsS0FBSyxNQUFNLENBQUMsR0FBRyxDQUFDLENBQUM7UUFDN0Qsa0NBQWtDO1FBQ2xDLElBQUksQ0FBQyxZQUFZLEVBQUU7WUFDakIsT0FBTyxPQUFPLENBQUM7U0FDaEI7UUFFRCxNQUFNLFdBQVcsR0FBRyxjQUFjLENBQUMsTUFBTSxFQUFFLFlBQVksQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLENBQUM7UUFFcEUsK0NBQStDO1FBQy9DLEtBQUssSUFBSSxLQUFLLEdBQUcsQ0FBQyxFQUFFLEtBQUssR0FBRyxXQUFXLEVBQUUsS0FBSyxFQUFFLEVBQUU7WUFDaEQsTUFBTSxTQUFTLEdBQUcsb0JBQW9CLENBQUMsTUFBTSxFQUFFLEtBQUssQ0FBQyxDQUFDO1lBQ3RELE1BQU0sV0FBVyxHQUFHLEtBQUssQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUMsQ0FBQyxHQUFHLEtBQUssU0FBUyxDQUFDLEdBQUcsQ0FBQyxDQUFDO1lBRS9ELElBQUksV0FBVyxFQUFFO2dCQUNmLE9BQU8sQ0FBQyxJQUFJLENBQUMsY0FBYyxDQUFDLFNBQVMsRUFBRSxXQUFXLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQzthQUM1RDtTQUNGLENBQUMsZUFBZTtRQUVqQixPQUFPLE9BQU8sQ0FBQztLQUNoQjtJQUVELElBQ0UsTUFBTSxDQUFDLE9BQU8sQ0FBQyxXQUFXLEVBQUUsS0FBSyxXQUFXO1FBQzVDLE1BQU0sQ0FBQyxPQUFPLENBQUMsV0FBVyxFQUFFLEtBQUssU0FBUyxFQUMxQztRQUNBLElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUMsRUFBRTtZQUN4QixNQUFNLFFBQVEsR0FBRyxLQUFLLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDLENBQUMsR0FBRyxLQUFLLE1BQU0sQ0FBQyxHQUFHLENBQUMsQ0FBQztZQUV6RCw2QkFBNkI7WUFDN0IsSUFBSSxDQUFDLFFBQVEsRUFBRTtnQkFDYixPQUFPLElBQUksQ0FBQzthQUNiO1lBRUQsT0FBTyxjQUFjLENBQUMsTUFBTSxFQUFFLFFBQVEsQ0FBQyxLQUFLLENBQUMsQ0FBQztTQUMvQztRQUVELE9BQU8sY0FBYyxDQUFDLE1BQU0sRUFBRSxLQUFLLENBQUMsQ0FBQztLQUN0QztJQUVELE9BQU8sQ0FBQyxLQUFLLENBQ1gsK0RBQStEO1FBQzdELE1BQU0sQ0FBQyxPQUFPO1FBQ2QsR0FBRyxDQUNOLENBQUM7SUFDRixPQUFPLElBQUksQ0FBQztBQUNkLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sVUFBVSxVQUFVLENBSXhCLElBQXlELEVBQ3pELE1BQTBCO0lBRTFCLE9BQU8sTUFBTSxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQyxNQUFNLENBQUMsQ0FBQyxXQUFXLEVBQUUsQ0FBQyxHQUFHLEVBQUUsS0FBSyxDQUFDLEVBQUUsRUFBRTtRQUMvRCxNQUFNLGFBQWEsR0FBRyxnQkFBZ0IsQ0FBQyxNQUFNLEVBQUUsR0FBRyxDQUFDLENBQUM7UUFFcEQsdUNBQ0ssV0FBVyxLQUNkLENBQUMsYUFBYSxDQUFDLElBQUksQ0FBQyxFQUFFLFNBQVMsQ0FDN0IsYUFBYSxFQUNiLEtBQUssQ0FDcUMsSUFDNUM7SUFDSixDQUFDLEVBQUUsRUFBUyxDQUFDLENBQUM7QUFDaEIsQ0FBQztBQUVEOzs7R0FHRztBQUNILE1BQU0sVUFBVSxVQUFVLENBSXhCLElBQXlELEVBQ3pELE1BQTBCO0lBRTFCLE9BQU8sTUFBTSxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQyxNQUFNLENBQUMsQ0FBQyxXQUFXLEVBQUUsQ0FBQyxHQUFHLEVBQUUsS0FBSyxDQUFDLEVBQUUsRUFBRTtRQUMvRCxNQUFNLGFBQWEsR0FBRyxnQkFBZ0IsQ0FBQyxNQUFNLEVBQUUsR0FBRyxDQUFDLENBQUM7UUFFcEQsV0FBVyxDQUFDLEdBQUcsQ0FBQyxHQUFHO1lBQ2pCLEtBQUssRUFBRSxTQUFTLENBQUMsYUFBYSxFQUFFLEtBQUssQ0FBQztZQUN0QyxHQUFHLEVBQUUsYUFBYSxDQUFDLEdBQUc7U0FDdkIsQ0FBQztRQUVGLE9BQU8sV0FBVyxDQUFDO0lBQ3JCLENBQUMsRUFBRSxFQUFTLENBQUMsQ0FBQztBQUNoQixDQUFDO0FBRUQsTUFBTSxVQUFVLGVBQWUsQ0FBQyxzQkFBc0I7SUFDcEQsTUFBTSxZQUFZLEdBQUcsY0FBYyxDQUFDLHNCQUFzQixDQUFDLENBQUM7SUFFNUQsSUFBSSxDQUFDLFlBQVksRUFBRTtRQUNqQixNQUFNLElBQUksS0FBSyxDQUNiLHdCQUF3QixzQkFBc0I7aUNBQ25CLDZCQUE2QjtPQUN2RCxDQUNGLENBQUM7S0FDSDtJQUVELE9BQU8sWUFBWSxDQUFDO0FBQ3RCLENBQUM7QUFFRCxNQUFNLFVBQVUsUUFBUSxDQUN0QixJQUFhLEVBQ2Isc0JBQWdEO0lBRWhELE1BQU0sWUFBWSxHQUFHLGVBQWUsQ0FBQyxzQkFBc0IsQ0FBQyxDQUFDO0lBRTdELE9BQU8sWUFBWSxDQUFDLE1BQU0sQ0FBQyxJQUFJLENBQUMsQ0FBQztBQUNuQyxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsZUFBZSxDQUM3QixJQUFJLEVBQ0osWUFBb0IsRUFDcEIscUJBQStDO0lBRS9DLE1BQU0sUUFBUSxHQUFHLFFBQVEsQ0FBQyxJQUFJLEVBQUUscUJBQXFCLENBQUMsQ0FBQztJQUV2RCxJQUFJLFFBQVEsS0FBSyxZQUFZLEVBQUU7UUFDN0IsT0FBTyxDQUFDLEtBQUssQ0FDWCx1Q0FBdUMsUUFBUSx1Q0FBdUMsWUFBWSxHQUFHLENBQ3RHLENBQUM7UUFDRixPQUFPLEtBQUssQ0FBQztLQUNkO0lBRUQsT0FBTyxJQUFJLENBQUM7QUFDZCxDQUFDO0FBRUQ7Ozs7Ozs7O0dBUUc7QUFDSCxNQUFNLFVBQVUsa0JBQWtCLENBQUMsV0FFbEM7SUFDQyxPQUFPLENBQ0wsTUFBTSxDQUFDLE9BQU8sQ0FBQyxXQUFXLENBQUM7U0FDeEIsTUFBTSxDQUFDLENBQUMsYUFBb0IsRUFBRSxDQUFDLEVBQUUsa0JBQWtCLENBQUMsRUFBRSxFQUFFO1FBQ3ZELElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxrQkFBa0IsQ0FBQyxLQUFLLENBQUMsRUFBRTtZQUMzQyxPQUFPLGFBQWEsQ0FBQyxNQUFNLENBQUMsa0JBQWtCLENBQUMsS0FBSyxDQUFDLENBQUM7U0FDdkQ7UUFDRCxhQUFhLENBQUMsSUFBSSxDQUFDO1lBQ2pCLEdBQUcsRUFBRSxrQkFBa0IsQ0FBQyxHQUFHO1lBQzNCLEtBQUssRUFBRSxrQkFBa0IsQ0FBQyxLQUFLO1NBQ2hDLENBQUMsQ0FBQztRQUNILE9BQU8sYUFBYSxDQUFDO0lBQ3ZCLENBQUMsRUFBRSxFQUFFLENBQUM7UUFDTix1RkFBdUY7U0FDdEYsSUFBSSxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUMsRUFBRSxFQUFFO1FBQ2IsSUFBSSxDQUFDLENBQUMsR0FBRyxHQUFHLENBQUMsQ0FBQyxHQUFHO1lBQUUsT0FBTyxDQUFDLENBQUMsQ0FBQztRQUM3QixPQUFPLENBQUMsQ0FBQyxHQUFHLEdBQUcsQ0FBQyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7SUFDL0IsQ0FBQyxDQUFDLENBQ0wsQ0FBQztBQUNKLENBQUMifQ==